import React, { useState, useRef } from 'react';
import { 
  Box, 
  Button, 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem, 
  Input, 
  Text
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { getCompany } from '../../Http/Index';

interface Company {
  _id: string;
  companyName: string;
}

interface CompanySelectorProps {
  selectedCompany: string;
  onCompanySelect: (companyId: string, companyName: string) => void;
  label?: string;
  width?: string;
  disabled?: boolean;
  placeholder?: string;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({
  selectedCompany,
  onCompanySelect,
  label = "Select Company:",
  width = "300px",
  disabled = false,
  placeholder = "Select a company"
}) => {
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState<string>('');
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const companyInputRef = useRef<HTMLInputElement>(null);

  const getallCompany = async () => {
    try {
      const response = await getCompany();
      const companies = response.data.data.map((company: any) => ({
        _id: company._id,
        companyName: company.companyName
      }));
      setCompanyList(companies);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const filteredCompanyList = companyList.filter(company =>
    company.companyName.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const handleCompanySelect = (company: Company) => {
    onCompanySelect(company._id, company.companyName);
    setCompanySearchTerm('');
    setIsCompanyMenuOpen(false);
  };

  return (
    <Box>
      <Text mb={2} fontWeight="bold">{label}</Text>
      <Menu isOpen={isCompanyMenuOpen} onClose={() => setIsCompanyMenuOpen(false)}>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          onClick={() => {
            if (!disabled) {
              getallCompany();
              setIsCompanyMenuOpen(!isCompanyMenuOpen);
            }
          }}
          width={width}
          textAlign="left"
          size="md"
          border="2px solid black"
          bg={disabled ? "gray.100" : "white"}
          _hover={{ bg: disabled ? "gray.100" : "gray.50" }}
          disabled={disabled}
          cursor={disabled ? "not-allowed" : "pointer"}
        >
          {selectedCompany || placeholder}
        </MenuButton>
        <MenuList maxH="300px" overflowY="auto">
          <Box p={2} onClick={(e) => e.stopPropagation()}>
            <Input
              ref={companyInputRef}
              placeholder="Search companies..."
              value={companySearchTerm}
              onChange={(e) => {
                setCompanySearchTerm(e.target.value);
                setTimeout(() => companyInputRef.current?.focus(), 0);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                }
              }}
              onFocus={(e) => e.stopPropagation()}
              size="sm"
              autoComplete="off"
            />
          </Box>
          {filteredCompanyList.map((company) => (
            <MenuItem
              key={company._id}
              onClick={() => handleCompanySelect(company)}
            >
              {company.companyName}
            </MenuItem>
          ))}
          {filteredCompanyList.length === 0 && (
            <MenuItem isDisabled>
              No companies found
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default CompanySelector;